/**
 * useTopicSync — BroadcastChannel-based cross-window sync for topics.
 *
 * When a topic is updated/focused in one window, other windows
 * (pop-outs or the main dashboard) stay in sync.
 */

import { useEffect, useRef } from 'react';

const CHANNEL_NAME = 'martha.topics.sync';

export type TopicSyncMessage =
  | { type: 'topic:focused'; topicId: string | null }
  | { type: 'topic:updated'; topicId: string }
  | { type: 'topic:created'; topicId: string }
  | { type: 'topic:removed'; topicId: string };

type SyncHandler = (msg: TopicSyncMessage) => void;

/** Broadcast a message to all other windows */
export function broadcastTopicEvent(msg: TopicSyncMessage): void {
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage(msg);
    ch.close();
  } catch {
    // BroadcastChannel not supported — silent fallback
  }
}

/** Listen for topic sync events from other windows */
export function useTopicSyncListener(handler: SyncHandler): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return; // not supported
    }

    const onMessage = (e: MessageEvent<TopicSyncMessage>) => {
      handlerRef.current(e.data);
    };

    ch.addEventListener('message', onMessage);
    return () => {
      ch.removeEventListener('message', onMessage);
      ch.close();
    };
  }, []);
}
